import { useForm } from "react-hook-form";
import { useEffect, useRef } from "react";
import Modal from "./Modal";
import Button from "../ui/Button";
import FormField from "../form/FormField";
import FormSubmit from "../form/FormSubmit";
import Dropdown from "../form/Dropdown";
import MultiDropdown from "../form/MultiDropdown";
import { FORM_IDS, ROLE_IDS } from "@utils";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";

function isClinicAssignmentRole(roleValue) {
  return (
    roleValue === ROLE_IDS.AREA_MANAGER ||
    roleValue === ROLE_IDS.PRACTITIONER_LEAD ||
    roleValue === ROLE_IDS.BOOKKEEPER
  );
}

function isPractitionerLeadRole(roleValue) {
  return roleValue === ROLE_IDS.PRACTITIONER_LEAD;
}

export default function UpdateInformation({
  isOpen,
  onClose,
  onUpdate,
  data,
  title = "Update Information",
  isLoading = false,
  fieldConfig = {}, // New prop for field-specific configuration
}) {
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    reset,
    setValue,
    formState: { errors },
  } = useForm();
  const prevIsOpenRef = useRef(false);
  const prevRoleRef = useRef(null);

  const { formTrackingProps, recordSubmitSuccess } = useFormInteractionTime(
    FORM_IDS.ADMIN_UPDATE_USER,
  );

  // Watch the role field to show/hide clinic field
  const roleValue = watch("role");

  // Clear clinic when role changes away from a clinic-assignment role.
  useEffect(() => {
    const normalizedRole = roleValue !== undefined ? Number(roleValue) : null;
    const previousRole = prevRoleRef.current !== undefined ? Number(prevRoleRef.current) : null;
    const isClinicRole = isClinicAssignmentRole(normalizedRole);
    const wasClinicRole = isClinicAssignmentRole(previousRole);

    if (prevRoleRef.current !== null && wasClinicRole && !isClinicRole) {
      setValue("clinic", [], { shouldDirty: true });
    }

    // Switching FROM Practitioner Lead TO another clinic role: re-wrap string → array
    if (
      prevRoleRef.current !== null &&
      isPractitionerLeadRole(previousRole) &&
      isClinicRole &&
      !isPractitionerLeadRole(normalizedRole)
    ) {
      const currentClinic = getValues("clinic");
      if (!Array.isArray(currentClinic)) {
        setValue("clinic", currentClinic ? [currentClinic] : [], { shouldDirty: true });
      }
    }

    // Switching TO Practitioner Lead: unwrap array → plain string
    if (isPractitionerLeadRole(normalizedRole)) {
      const currentClinic = getValues("clinic");
      if (Array.isArray(currentClinic)) {
        setValue("clinic", currentClinic[0] ?? "", { shouldDirty: true });
      }
    }

    prevRoleRef.current = normalizedRole;
  }, [roleValue, setValue, getValues]);

  // Set form values only when modal first opens (transitions from closed to open)
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current && data) {
      // Reset form with new data only when modal opens
      const formData = {};
      Object.keys(data).forEach((key) => {
        if (key !== "id" && key !== "created_at" && key !== "updated_at") {
          formData[key] = data[key];
        }
      });
      reset(formData);
      prevRoleRef.current = data.role;
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, data, reset]);

  const onSubmit = async (formData) => {
    // Convert role to number if it's a string
    if (formData.role) {
      formData.role = Number(formData.role);
    }

    // Remove read-only fields that the backend ignores
    delete formData.full_name;

    // Ensure first_name and last_name are trimmed
    if (formData.first_name) formData.first_name = formData.first_name.trim();
    if (formData.last_name) formData.last_name = formData.last_name.trim();

    if (!isClinicAssignmentRole(formData.role)) {
      formData.clinic = [];
    } else if (formData.role === ROLE_IDS.PRACTITIONER_LEAD) {
      const selectedClinics = Array.isArray(formData.clinic)
        ? formData.clinic
        : formData.clinic
          ? [formData.clinic]
          : [];
      formData.clinic = selectedClinics.slice(0, 1);
    } else if (!Array.isArray(formData.clinic)) {
      formData.clinic = formData.clinic ? [formData.clinic] : [];
    }

    try {
      await onUpdate(formData);
      recordSubmitSuccess();
    } catch (error) {
      console.error("Failed to update user:", error);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <FormSubmit onSubmit={handleSubmit(onSubmit)} className="space-y-6" {...formTrackingProps}>
        {/* Form Fields */}
        <div className="space-y-4">
          {data &&
            Object.keys(data).map((key) => {
              // Skip certain fields that shouldn't be editable
              if (key === "id" || key === "created_at" || key === "updated_at") {
                return null;
              }

              const normalizedRole = roleValue !== undefined ? Number(roleValue) : null;

              if (key === "clinic" && !isClinicAssignmentRole(normalizedRole)) {
                return null;
              }

              // Check if this field has custom configuration
              const config = fieldConfig[key];

              // Determine if field is required
              let isRequired = config?.required || false;

              // Make name and email always required
              if (key === "first_name" || key === "last_name" || key === "email") {
                isRequired = true;
              }

              if (key === "clinic" && isClinicAssignmentRole(normalizedRole)) {
                isRequired = true;
              }

              // Render Dropdown for fields with options
              if (config && config.type === "dropdown") {
                return (
                  <Dropdown
                    key={key}
                    label={
                      config.label ||
                      key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
                    }
                    name={key}
                    options={config.options || []}
                    placeholder={config.placeholder}
                    register={register}
                    error={errors[key]?.message}
                    required={isRequired}
                  />
                );
              }

              // Render MultiDropdown for multi-select fields
              // Exception: clinic field for Practitioner Lead uses single Dropdown
              if (config && config.type === "multi-dropdown") {
                if (key === "clinic" && isPractitionerLeadRole(normalizedRole)) {
                  return (
                    <Dropdown
                      key={key}
                      label={
                        config.label ||
                        key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
                      }
                      name={key}
                      options={config.options || []}
                      placeholder={config.placeholder}
                      register={register}
                      error={errors[key]?.message}
                      required={isRequired}
                    />
                  );
                }

                return (
                  <MultiDropdown
                    key={key}
                    label={
                      config.label ||
                      key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
                    }
                    name={key}
                    options={config.options || []}
                    placeholder={config.placeholder}
                    setValue={setValue}
                    watch={watch}
                    error={errors[key]?.message}
                    required={isRequired}
                  />
                );
              }

              // Default to FormField
              return (
                <FormField
                  key={key}
                  label={key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                  name={key}
                  type={key === "email" ? "email" : key === "phone_number" ? "tel" : "text"}
                  register={register}
                  error={errors[key]?.message}
                  required={isRequired}
                />
              );
            })}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-palette-gray-divider">
          <Button type="button" variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading ? "Updating..." : "Update"}
          </Button>
        </div>
      </FormSubmit>
    </Modal>
  );
}
